/**
 * Shared scoring primitives used by every format module.
 */

import type {
  HoleScoreValue,
  PlayerId,
  RoundContext,
  TiebreakCounters,
  UnitId,
} from './types'
import { EMPTY_COUNTERS } from './types'

/**
 * What a player's score contributes on a hole.
 *
 * `forfeit` covers picking up and being conceded: the player cannot win the
 * hole, but the hole HAS been played, which is the distinction a plain
 * `number | null` cannot express. `pending` means nothing has been entered yet.
 */
export interface Ball {
  net: number | null
  gross: number | null
  forfeit: boolean
  pending: boolean
}

export const PENDING: Ball = { net: null, gross: null, forfeit: false, pending: true }

export function ballFor(value: HoleScoreValue | undefined, pops: number): Ball {
  if (!value) return PENDING
  if (value.state === 'holed') {
    return { net: value.gross - pops, gross: value.gross, forfeit: false, pending: false }
  }
  if (value.state === 'pickedUp') {
    /*
     * A most-likely score keeps the hole usable for STROKE PLAY, but the man
     * still cannot win the hole in match play — and it is worth nothing at all
     * in quota, which is `quotaPointsForHole`'s own business and does not come
     * through here. This comment used to claim quota read it too, which was
     * never true: quota pays for holes you hole out, walking in is a double
     * bogey, and a double bogey is worth no points. Scott's ruling, 18 Aug.
     */
    const gross = value.mostLikely
    return {
      net: gross == null ? null : gross - pops,
      gross,
      forfeit: true,
      pending: false,
    }
  }
  return { net: null, gross: null, forfeit: true, pending: false }
}

/**
 * Compare two balls for match play. Returns +1 if A wins the hole, -1 if B
 * wins, 0 if halved, null if the hole has not been played out.
 */
export function compareBalls(a: Ball, b: Ball): number | null {
  if (a.pending && b.pending) return null
  // One side has posted and the other has not: the hole is still open.
  if (a.pending || b.pending) {
    const posted = a.pending ? b : a
    if (!posted.forfeit) return null
    // ...unless the side that HAS acted forfeited, which settles it either way.
    return a.pending ? 1 : -1
  }
  if (a.forfeit && b.forfeit) return 0
  if (a.forfeit) return -1
  if (b.forfeit) return 1
  if (a.net == null || b.net == null) return null
  if (a.net < b.net) return 1
  if (a.net > b.net) return -1
  return 0
}

/** Tally a score against par for the tiebreak chain. */
export function tallyInto(
  counters: TiebreakCounters,
  value: HoleScoreValue | undefined,
  par: number,
  pops: number,
  basis: 'gross' | 'net',
): void {
  if (!value) return

  /*
   * A hole that was played but not finished still counts.
   *
   * Skipping it altogether rewarded not finishing: a man who picked up eight
   * times recorded eight of nothing, while his opponent's eight honest bogeys
   * all counted against him on the "fewest bogeys" step. Picking up bought a
   * cleaner card than playing.
   *
   * A pick-up with a most-likely score is judged on that number — the same one
   * stroke play posts, so the card and the counters cannot disagree. A pick-up
   * with no number, and a conceded hole, have no score to judge: they are
   * counted as worse than a bogey, which is what walking in means.
   */
  if (value.state !== 'holed') {
    const guess = value.state === 'pickedUp' ? value.mostLikely : null
    if (guess == null) {
      counters.worse += 1
      return
    }
    const dd = (basis === 'net' ? guess - pops : guess) - par
    if (dd <= -2) counters.eagles += 1
    else if (dd === -1) counters.birdies += 1
    else if (dd === 0) counters.pars += 1
    else if (dd === 1) counters.bogeys += 1
    else counters.worse += 1
    return
  }

  const score = basis === 'net' ? value.gross - pops : value.gross
  const d = score - par
  if (d <= -2) counters.eagles += 1
  else if (d === -1) counters.birdies += 1
  else if (d === 0) counters.pars += 1
  else if (d === 1) counters.bogeys += 1
  else counters.worse += 1
}

export function emptyCounters(): TiebreakCounters {
  return { ...EMPTY_COUNTERS }
}

export function countersForPlayers(
  ctx: RoundContext,
  playerIds: PlayerId[],
  basis: 'gross' | 'net',
): Record<PlayerId, TiebreakCounters> {
  const out: Record<PlayerId, TiebreakCounters> = {}
  for (const pid of playerIds) {
    const c = emptyCounters()
    ctx.holes.forEach((hole, i) => {
      tallyInto(c, ctx.scores[pid]?.[i], hole.par, ctx.pops[pid]?.[i] ?? 0, basis)
    })
    out[pid] = c
  }
  return out
}

/** How many holes of this round have been played out by a set of players. */
export function thruFor(ctx: RoundContext, playerIds: PlayerId[]): number {
  let thru = 0
  for (let i = 0; i < ctx.holes.length; i++) {
    const anyPlayed = playerIds.some((pid) => ctx.scores[pid]?.[i] !== undefined)
    if (!anyPlayed) break
    thru = i + 1
  }
  return thru
}

/**
 * Holes that were skipped rather than not yet reached.
 *
 * `thruFor` stops at the first gap, so a card with the 7th missing and the 9th
 * entered reports "thru 6" — which is indistinguishable on screen from a group
 * that is genuinely standing on the 7th tee. The difference matters: one is a
 * group playing golf and the other is a hole nobody is going to remember by the
 * time the cards are counted.
 *
 * So a hole counts as missing only when something LATER has been entered. That
 * is what makes it a gap rather than simply the future, and it is why this
 * cannot be derived from `thru` alone.
 *
 * Returns hole numbers as printed on the card, not indices — every caller is
 * about to show them to somebody.
 */
export function missingHoles(ctx: RoundContext, playerIds: PlayerId[]): number[] {
  let furthest = -1
  for (let i = 0; i < ctx.holes.length; i++) {
    if (playerIds.some((pid) => ctx.scores[pid]?.[i] !== undefined)) furthest = i
  }
  if (furthest < 0) return []

  const gaps: number[] = []
  for (let i = 0; i < furthest; i++) {
    const missing = playerIds.some((pid) => ctx.scores[pid]?.[i] === undefined)
    if (missing) gaps.push(ctx.holes[i]!.number)
  }
  return gaps
}

export function unitId(...parts: string[]): UnitId {
  return parts.join('~')
}

export const sum = (xs: number[]): number => xs.reduce((a, b) => a + b, 0)
